import React, { useEffect, useRef } from "react"

import { classnames } from '@cgtrader/triangle-ui/dist/src/ssr-index'
import { number, func, bool, arrayOf } from "prop-types"

import { EMPTY_ARRAY, NOOP } from 'Utils/stableDefaults'
import useBreakpoint from "Utils/useBreakpoint"

import EditModeForm from "./Components/EditModeForm/EditModeForm"
import MediaCarousel from "./Components/MediaCarousel/MediaCarousel"
import TopActions from "./Components/TopActions/TopActions"
import UseCaseInfo from "./Components/UseCaseInfo/UseCaseInfo"
import useMediaCarousel from "./hooks/useMediaCarousel"
import useUseCaseEdit from "./hooks/useUseCaseEdit"
import { useCaseShape, printerTypeOptionShape } from "../../Utils/propTypes"

function UseCaseMediaViewer({
  useCase,
  onUseCaseClick = NOOP,
  initialMediaIndex = 0,
  canDelete,
  canEdit,
  canUseLikeFeature,
  isFetchingPrinterTypes = false,
  onDelete,
  onEditUseCase,
  onLike,
  onUnlike,
  printerTypeOptions = EMPTY_ARRAY,
  isGalleryView = false,
  canDownloadSlicerFile = true,
  isOwner = false,
  onShowNotification,
  isUpdating = false,
  onCloseGallery = null,
  onEditingChange = null,
}) {
  const breakpoint = useBreakpoint()
  const isMobile = breakpoint === 'xs' || breakpoint === 'sm'
  const mediaCarouselNavRef = useRef(null)

  const {
    currentIndex,
    handlePrevious,
    handleNext,
    handleDotClick,
  } = useMediaCarousel({
    useCaseId: useCase.id,
    initialMediaIndex,
    embedsCount: useCase.useCaseEmbeds.length,
    isGalleryView,
    keydownTargetRef: mediaCarouselNavRef,
  })

  const {
    isEditing,
    isFormReady,
    control: editControl,
    mediaChanges,
    canSave,
    handleEditClick,
    handleSaveClick,
    handleCancelClick,
    handleAddMedia,
    handleRemoveExistingMedia,
    handleRemoveNewMedia,
  } = useUseCaseEdit({
    useCase,
    printerTypeOptions,
    onEditUseCase,
    isUpdating,
    onShowNotification,
    onCloseGallery,
    onEditingChange,
  })

  const showTopActions = canDelete || canEdit || canUseLikeFeature

  const handleMediaClick = () => {
    if (isGalleryView || isMobile) return
    onUseCaseClick(useCase, currentIndex)
  }

  const handleMediaWrapperClick = (e) => {
    if (!isGalleryView && !isMobile) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  useEffect(() => {
    if (!isGalleryView) {
      return
    }

    mediaCarouselNavRef.current?.focus({ preventScroll: true })
  }, [isGalleryView, useCase.id])

  return (
    <div className="use-case-media-card">
      {isEditing && isFormReady && (
        <EditModeForm
          control={editControl}
          availablePrinterTypes={printerTypeOptions}
          isFetchingPrinterTypes={isFetchingPrinterTypes}
          useCase={useCase}
          mediaChanges={mediaChanges}
          onAddMedia={handleAddMedia}
          onRemoveExistingMedia={handleRemoveExistingMedia}
          onRemoveNewMedia={handleRemoveNewMedia}
          onShowNotification={onShowNotification}
        />
      )}

      <div
        className={classnames(
          'use-case-media-card__viewer',
          isGalleryView && isEditing && 'use-case-media-card__viewer--editing'
        )}
        onClick={handleMediaClick}
        style={{ cursor: isGalleryView || isMobile ? 'default' : 'pointer' }}
      >
        {showTopActions && (
          <TopActions
            canDelete={canDelete}
            canEdit={canEdit}
            canUseLikeFeature={canUseLikeFeature}
            isEditing={isEditing}
            isUpdating={isUpdating}
            useCaseId={useCase.id}
            likeCount={useCase.likeCount}
            isLiked={useCase.liked}
            itemId={useCase.itemId}
            isLoading={useCase.isLoading}
            onDelete={onDelete}
            onEdit={handleEditClick}
            onSave={handleSaveClick}
            onCancel={handleCancelClick}
            onLike={onLike}
            onUnlike={onUnlike}
            canSave={canSave}
          />
        )}

        <MediaCarousel
          ref={mediaCarouselNavRef}
          embeds={useCase.useCaseEmbeds}
          currentIndex={currentIndex}
          isGalleryView={isGalleryView}
          isMobile={isMobile}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onDotClick={handleDotClick}
          onMediaClick={handleMediaWrapperClick}
        />
      </div>

      <UseCaseInfo
        username={useCase.username}
        createdAt={useCase.createdAt}
        isGalleryView={isGalleryView}
        description={useCase.description}
        embedsCount={useCase.useCaseEmbeds.length}
        technology={useCase.technology}
        printerType={useCase.printerType}
        avatarUrl={useCase.userAvatar}
        slicesDetails={useCase.slicesDetails}
        material={useCase.material}
        printSuccess={useCase.printSuccess}
        printTime={useCase.printTime}
        usedSupport={useCase.usedSupport}
        canDownloadSlicerFile={canDownloadSlicerFile}
        isOwner={isOwner}
      />
    </div>
  )
}

UseCaseMediaViewer.propTypes = {
  initialMediaIndex: number,
  canDelete: bool.isRequired,
  canEdit: bool.isRequired,
  canUseLikeFeature: bool.isRequired,
  isFetchingPrinterTypes: bool,
  onDelete: func.isRequired,
  onEditUseCase: func.isRequired,
  onLike: func.isRequired,
  onUnlike: func.isRequired,
  isGalleryView: bool,
  onUseCaseClick: func,
  useCase: useCaseShape.isRequired,
  printerTypeOptions: arrayOf(printerTypeOptionShape),
  canDownloadSlicerFile: bool,
  isOwner: bool,
  onShowNotification: func.isRequired,
  isUpdating: bool,
  onCloseGallery: func,
  onEditingChange: func,
}

export default UseCaseMediaViewer
